import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import Editor from '@components/editor/Editor';
import ModalContainer from '@components/modals/ModalContainer';

import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import { uploadDocument } from '@services/commonService';

import { toastError, toastSuccess } from '@/components/Toast';
import CommonImage from '@/components/CommonImage';
import CustomDropdown from '@/components/CustomDropdown';
import { useTaskConfigurations } from '@/store/reducers';
import {
	useCreateSopMutation,
	useGetSopByIdQuery,
	useUpdateSopMutation
} from '@/features/task-configuration/api/taskConfigurationApi';
import { SOP_LINK_TYPES } from '@/features/task-configuration/constants';

const EMPTY_LINK = { type: '', title: '', link: '' };

const INITIAL_STATE = {
	stageId: null,
	stageName: '',
	sopName: '',
	whichStylesAreYouSeeingHere: '',
	summarySop: '',
	links: [{ ...EMPTY_LINK }],
	files: []
};

const CreateAndEditSop = ({ onClose, sopId = null }) => {
	const isEditMode = Boolean(sopId);
	const [formData, setFormData] = useState(INITIAL_STATE);
	const [errors, setErrors] = useState(null);
	const [stylesEditorValue, setStylesEditorValue] = useState('');
	const [stylesEditorKey, setStylesEditorKey] = useState('styles-editor-new');
	const [summaryEditorValue, setSummaryEditorValue] = useState('');
	const [summaryEditorKey, setSummaryEditorKey] = useState('summary-editor-new');
	const { stageList = [] } = useTaskConfigurations();
	const dispatch = useDispatch();
	const [createSop] = useCreateSopMutation();
	const [updateSop] = useUpdateSopMutation();
	const { data: sopData } = useGetSopByIdQuery({ id: sopId }, { skip: !sopId });

	useEffect(() => {
		if (!isEditMode) {
			setStylesEditorValue('');
			setStylesEditorKey('styles-editor-new');
			setSummaryEditorValue('');
			setSummaryEditorKey('summary-editor-new');
		}
	}, [isEditMode]);

	useEffect(() => {
		if (sopData && isEditMode) {
			const stylesContent = sopData.description || '';
			const summaryContent = sopData.sop || '';
			const hyperlinkAttachments = (sopData.attachments || [])
				.filter((att) => att.type === 'HYPERLINK')
				.map((att) => ({
					type: SOP_LINK_TYPES.find((t) => t.id === att.linkType)?.value || att.linkType,
					title: att.title || '',
					link: att.url || ''
				}));

			const documentAttachments = (sopData.attachments || [])
				.filter((att) => att.type === 'DOCUMENT')
				.map((att) => ({
					name: `Document ${att.documentId}`,
					type: 'TASK_DASHBOARD_SOP_ATTACHMENT',
					url: '',
					documentId: att.documentId
				}));

			setFormData({
				stageId: sopData.stage?.id || null,
				stageName: sopData.stage?.name || '',
				sopName: sopData.title || '',
				whichStylesAreYouSeeingHere: stylesContent,
				summarySop: summaryContent,
				links: hyperlinkAttachments.length > 0 ? hyperlinkAttachments : [{ ...EMPTY_LINK }],
				files: documentAttachments
			});
			setStylesEditorValue(stylesContent);
			setStylesEditorKey(`styles-editor-${sopId || 'new'}`);
			setSummaryEditorValue(summaryContent);
			setSummaryEditorKey(`summary-editor-${sopId || 'new'}`);
		}
	}, [sopData, isEditMode]);

	const handleChange = (e) => {
		setFormData((prev) => ({
			...prev,
			[e.target.name]: e.target.value
		}));
		setErrors((prev) => ({
			...prev,
			[e.target.name]: ''
		}));
	};

	const validateForm = () => {
		const newErrors = {};
		if (!formData.stageName) {
			newErrors.stageName = 'Stage name is required';
		}
		if (!formData.sopName) {
			newErrors.sopName = 'SOP name is required';
		}
		formData.links.forEach((link, index) => {
			if ((link.title || link.link) && !link.type) {
				newErrors[`linkType_${index}`] = 'Type is required';
			}
		});
		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleAddLink = () => {
		setFormData((prev) => ({
			...prev,
			links: [...prev.links, { ...EMPTY_LINK }]
		}));
	};

	const handleLinkChange = (index, field, value) => {
		setFormData((prev) => ({
			...prev,
			links: prev.links.map((link, i) => {
				if (i !== index) return link;
				const updated = { ...link, [field]: value };
				if (field === 'type' && (!link.title || link.title === link.type)) {
					updated.title = value;
				}
				return updated;
			})
		}));
		if (field === 'type') {
			setErrors((prev) => ({
				...prev,
				[`linkType_${index}`]: ''
			}));
		}
	};

	const handleDeleteLink = (index) => {
		setFormData((prev) => ({
			...prev,
			links: prev.links.filter((_, i) => i !== index)
		}));
	};

	const handleFileUpload = async (e) => {
		const selectedFiles = Array.from(e.target.files);
		if (selectedFiles.length === 0) return;
		e.target.value = '';

		for (const file of selectedFiles) {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = async () => {
				try {
					dispatch(openLoader());
					const postData = {
						name: file.name,
						docMimeType: file.type,
						documentType: 'TASK_DASHBOARD_SOP_ATTACHMENT',
						base64Str: reader.result
					};
					const data = await uploadDocument(postData);
					if (data) {
						setFormData((prev) => ({
							...prev,
							files: [
								...prev.files,
								{
									name: file.name,
									type: data?.type || 'TASK_DASHBOARD_SOP_ATTACHMENT',
									url: data?.url || '',
									documentId: data?.documentId || data?.id || 0
								}
							]
						}));
					}
					dispatch(closeLoader());
				} catch {
					dispatch(closeLoader());
					toastError(`Failed to upload ${file.name}`);
				}
			};
		}
	};

	const handleDeleteFile = (index) => {
		setFormData((prev) => ({
			...prev,
			files: prev.files.filter((_, i) => i !== index)
		}));
	};

	const truncateFileName = (name, maxLength = 12) => {
		if (name.length <= maxLength) return name;
		const ext = name.lastIndexOf('.') !== -1 ? name.slice(name.lastIndexOf('.')) : '';
		const baseName = name.slice(0, name.lastIndexOf('.') !== -1 ? name.lastIndexOf('.') : name.length);
		return `${baseName.slice(0, maxLength - ext.length - 1)}…${ext}`;
	};

	const handleSubmit = async () => {
		if (!validateForm()) {
			return;
		}

		try {
			const fileAttachments = formData.files.map((file) => ({
				type: 'DOCUMENT',
				url: null,
				documentId: file.documentId
			}));

			const linkAttachments = formData.links
				.filter((link) => link.type || link.title || link.link)
				.map((link) => ({
					type: 'HYPERLINK',
					linkType: SOP_LINK_TYPES.find((t) => t.value === link.type)?.id || link.type,
					title: link.title,
					url: link.link,
					documentId: null
				}));

			const payload = {
				title: formData.sopName,
				description: formData.whichStylesAreYouSeeingHere,
				sop: formData.summarySop,
				stageId: formData.stageId,
				attachments: [...fileAttachments, ...linkAttachments]
			};

			if (isEditMode) {
				await updateSop({ body: payload, id: sopId }).unwrap();
				toastSuccess('SOP updated successfully');
			} else {
				await createSop({ body: payload }).unwrap();
				toastSuccess('SOP created successfully');
			}
			onClose();
		} catch (error) {
			toastError(error?.data?.message || 'Something went wrong');
		}
	};

	return (
		<ModalContainer className="sop-tutorials-modal" onCloseModal={onClose}>
			<div className="common-popup sop-tutorials-modal body-scroll">
				<div className="common-popup-header d-flex justify-content-between">
					<div className="popup-tilte">
						<h3 className="semibold-16 mb-0">
							{isEditMode ? 'Edit' : 'Create'} Journey SOP & Tutorials
						</h3>
					</div>
					<div className="close-btn" onClick={onClose}>
						<img src={`${process.env.PUBLIC_URL}/icons/close.svg`} alt="close" />
					</div>
				</div>
				<div className="common-popup-body scroll-y-label">
					<div className="row">
						<div className="col-md-12">
							<div className="form-group">
								<label htmlFor="collection">Stage name*</label>
								<CustomDropdown
									title="Section"
									onItemClick={(item) => {
										setFormData((prev) => ({
											...prev,
											stageId: item?.id,
											stageName: item?.value
										}));
										setErrors((prev) => ({
											...prev,
											stageName: ''
										}));
									}}
									selectedItem={formData.stageName}
									items={stageList?.map((item) => ({
										...item,
										id: item?.stageId,
										value: item?.stageName,
										name: item?.stageName
									}))}
								/>
								{errors?.stageName && <p className="error">{errors.stageName}</p>}
							</div>
						</div>
						<div className="col-md-12 pr-2">
							<div className="form-group">
								<label htmlFor="collection">SOP name*</label>
								<input
									value={formData.sopName}
									onChange={(e) => handleChange(e)}
									name="sopName"
									type="text"
									placeholder="Title"
								/>
								{errors?.sopName && <p className="error">{errors.sopName}</p>}
							</div>
						</div>
						<div className="col-md-12">
							<div className="form-group">
								<label htmlFor="collection">Which styles are you seeing here?</label>
								<div className="techpack-editor mt-2">
									<Editor
										key={stylesEditorKey}
										setEditorContent={(content) => {
											setStylesEditorValue(content || '');
											setFormData((prev) => ({
												...prev,
												whichStylesAreYouSeeingHere: content || ''
											}));
										}}
										setMentionIds={() => {}}
										defaultMention={stylesEditorValue}
									/>
								</div>
							</div>
						</div>
						<div className="col-md-12">
							<div className="form-group">
								<label htmlFor="collection">Summary SOP</label>
								<div className="techpack-editor mt-2">
									<Editor
										key={summaryEditorKey}
										setEditorContent={(content) => {
											setSummaryEditorValue(content || '');
											setFormData((prev) => ({
												...prev,
												summarySop: content || ''
											}));
										}}
										setMentionIds={() => {}}
										defaultMention={summaryEditorValue}
									/>
								</div>
							</div>
						</div>
						<div className="col-md-12">
							<div>
								<div className="add-new-design">
									<button
										type="button"
										className="button text size32 px-0"
										onClick={handleAddLink}
									>
										<span className="mr-1 gray_dark_01 semibold-14">Links</span>
										<CommonImage src="/icons/add-round.svg" alt="add" />
									</button>
								</div>
								<div className="tutorials-table">
									<table className="table with-border scroll-x-label">
										<thead>
											<tr>
												<th>Type</th>
												<th>Title</th>
												<th>Link </th>
												<th>Action</th>
											</tr>
										</thead>
										<tbody>
											{formData.links.map((link, index) => (
												<tr key={index}>
													<td className="type-column">
														<CustomDropdown
															title="Type"
															onItemClick={(item) =>
																handleLinkChange(index, 'type', item?.value)
															}
															selectedItem={link.type}
															items={SOP_LINK_TYPES}
															isShowSearch={false}
														/>
														{errors?.[`linkType_${index}`] && (
															<p className="error">
																{errors[`linkType_${index}`]}
															</p>
														)}
													</td>
													<td className="title-column">
														<input
															className="no-border"
															type="text"
															placeholder="Title"
															value={link.title}
															onChange={(e) =>
																handleLinkChange(
																	index,
																	'title',
																	e.target.value
																)
															}
														/>
													</td>
													<td className="link-column">
														<input
															className="no-border"
															type="text"
															placeholder="Link"
															value={link.link}
															onChange={(e) =>
																handleLinkChange(
																	index,
																	'link',
																	e.target.value
																)
															}
														/>
													</td>
													<td className="action-column">
														<div className="option">
															<div className="dropdown">
																<button
																	aria-expanded="false"
																	className="btn btn-default dropdown-toggle"
																	data-toggle="dropdown"
																	id={`menu-${index}`}
																	type="button"
																>
																	<CommonImage
																		src="/icons/more_icon.svg"
																		alt="more_icon"
																	/>
																</button>
																<ul
																	aria-labelledby={`menu-${index}`}
																	className="dropdown-menu dropdown-menu-right shadow-lg"
																	role="menu"
																	x-placement="bottom-end"
																>
																	<li
																		className="px-4 pb-3"
																		role="presentation"
																	>
																		<a
																			className="font-weight-normal  text-black"
																			href
																			role="menuitem"
																			tabIndex={-1}
																			onClick={() =>
																				handleDeleteLink(index)
																			}
																		>
																			Delete
																		</a>
																	</li>
																</ul>
															</div>
														</div>
													</td>
												</tr>
											))}
										</tbody>
									</table>
									<div className="mb-4">
										<button className="button outline size32" onClick={handleAddLink}>
											Add new
										</button>
									</div>
								</div>
								<p className="semibold-14 gray_dark_01 mb-12">Files</p>
								<div className="all-files-container d-flex flex-wrap align-items-center gap-2">
									<div className="form-group w-25 mb-0">
										<div className="inline-upload">
											<label
												htmlFor="multipleUpload"
												className="regular-14 border-gray_dark_01"
											>
												<CommonImage src="/icons/upload-dark.svg" /> Upload
											</label>
											<input
												type="file"
												className="file-upload"
												id="multipleUpload"
												name="file"
												accept="*"
												multiple
												onChange={handleFileUpload}
											/>
										</div>
									</div>

									{formData.files.map((file, index) => (
										<div
											key={index}
											className="single-pdf-file d-flex align-items-center"
										>
											<span className="regular-14">{truncateFileName(file.name)}</span>
											<div
												className="acton-img cursor-pointer ml-1"
												onClick={() => handleDeleteFile(index)}
											>
												<CommonImage src="/icons/delete_red.svg" width={16} />
											</div>
										</div>
									))}
								</div>
							</div>
						</div>
					</div>
				</div>
				<div className="modal-footer">
					<div className="footer-buttons text-right">
						<button className="button outline size32 ml-2" onClick={onClose}>
							Cancel
						</button>
						<button className="button size32 ml-2" onClick={handleSubmit}>
							Submit
						</button>
					</div>
				</div>
			</div>
		</ModalContainer>
	);
};

export default CreateAndEditSop;
